import React, { useState, useRef, useEffect } from 'react';
import { Button, Typography, Box, CircularProgress } from '@mui/material';
import { FaUpload } from 'react-icons/fa';
import DocumentDataPopup from './DocumentDataPopup';
import { toast } from 'react-toastify';

const DOC_CONFIG = {
  VendorInvoice: {
    title: 'Supplier Invoice',
    modes: {
      Direct: {
        processApi: 'https://cd9d-205-254-163-120.ngrok-free.app/process-fb-invoice',
        submitApi: 'https://cd9d-205-254-163-120.ngrok-free.app/submit-fb-invoice',
      },
      MIRO: {
        processApi: 'https://cd9d-205-254-163-120.ngrok-free.app/process-miro-invoice',
        submitApi: 'https://cd9d-205-254-163-120.ngrok-free.app/submit-miro-invoice',
      },
    },
    formDataKey: 'invoice_pdf',
  },
  PurchaseOrder: {
    title: 'Purchase Order',
    processApi: 'https://cd9d-205-254-163-120.ngrok-free.app/process-quotation',
    submitApi: 'https://cd9d-205-254-163-120.ngrok-free.app/submit_po',
    formDataKey: 'quotation_pdf',
  },
  GRN: {
    title: 'GRN',
    processApi: 'https://cd9d-205-254-163-120.ngrok-free.app/process-grn',
    submitApi: 'https://cd9d-205-254-163-120.ngrok-free.app/submit_grn',
    formDataKey: 'grn_pdf',
  },
};

const SupplierInvoiceUpload = ({ documentType }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Default to MIRO ("With PO"), the common case: most vendor invoices here
  // reference a purchase order. The Direct / "Without PO" path posts FB60 and is
  // live again as of 2026-08-27 (ZFB60_POST_SRV replaced the never-activated
  // ZZFB60_VENDOR_INVOICE1_SRV, which used to make every Direct submit 503).
  const [mode, setMode] = useState('MIRO');
  const fileInputRef = useRef(null);

  const config = DOC_CONFIG[documentType];

  {/* ✅ ONLY NECESSARY ADDITION */}
  useEffect(() => {
    window.__UPLOAD_PANEL_ACTIVE = true;
    return () => {
      window.__UPLOAD_PANEL_ACTIVE = false;
    };
  }, []);

  // ----------------- ORIGINAL CODE BELOW -----------------

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert('Please select the invoice file.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append(config.formDataKey, selectedFile);

    try {
      const processUrl =
        documentType === 'VendorInvoice'
          ? config.modes[mode].processApi
          : config.processApi;

      const response = await fetch(processUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          const backendMessage =
            Array.isArray(errorData.details) && errorData.details.length > 0
              ? errorData.details.join(', ')
              : errorData.message || `Failed to extract data from the ${config.title}.`;

          toast.error(backendMessage);
        } catch {
          toast.error(`Failed to extract data from the ${config.title}.`);
        }

        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setPopupData(data);
      setIsPopupOpen(true);
    } catch (error) {
      toast.error(`An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupData(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handlePopupSubmit = async () => {
    if (!popupData) return;

    try {
      const apiUrl =
        documentType === 'VendorInvoice'
          ? config.modes[mode].submitApi
          : config.submitApi;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(popupData),
      });

      let result;
      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (!response.ok) {
        // The invoice endpoints answer with {details: {main_message}}, while the
        // PO and GRN endpoints raise HTTPException, which FastAPI serialises as
        // {detail: "..."}. Without the second case the SAP error is dropped and
        // every PO failure reads "Failed to submit Purchase Order".
        const backendMessage =
          result.details?.main_message ||
          (typeof result.detail === 'string' ? result.detail : null) ||
          result.message;

        toast.error(`Error: ${backendMessage || `Failed to submit ${config.title}`}`);
        return;
      }

      if (result === 'ISSUE') {
        toast.error('Unable to create Invoice. Check PO number properly.');
      } else {
        const docNum = result?.sap_details?.document_number;
        if (docNum) {
          toast.success(`Invoice posted successfully to SAP. Document No: ${docNum}`);
        } else {
          toast.success(result.message || 'Invoice posted successfully to SAP');
        }
      }

      handlePopupClose();
    } catch (error) {
      toast.error(`Submission failed: ${error.message}`);
    }
  };

  return (
    <Box textAlign="center" sx={{ p: 1.5, mt: 0.1, mb: 0.1 }}>
      <Typography variant="h5" gutterBottom>
        Upload {config.title}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={2}>
        Please upload the {config.title} file to pre-fill and validate data.
      </Typography>

      <Button
        component="label"
        variant="contained"
        startIcon={<FaUpload />}
        sx={{ mb: 2 }}
      >
        {selectedFile ? selectedFile.name : 'Select File'}
        <input
          type="file"
          hidden
          accept="application/pdf"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
      </Button>

      {documentType === 'VendorInvoice' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
          <Button
            variant={mode === 'Direct' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setMode('Direct')}
          >
            Without PO
          </Button>
          <Button
            variant={mode === 'MIRO' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setMode('MIRO')}
          >
            With PO
          </Button>
        </Box>
      )}

      {selectedFile && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isLoading}
            sx={{ minWidth: '120px' }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Process ${config.title}`
            )}
          </Button>
        </Box>
      )}

      {popupData && (
        <DocumentDataPopup
          open={isPopupOpen}
          onClose={handlePopupClose}
          data={popupData}
          onSubmit={handlePopupSubmit}
          documentType={documentType}
        />
      )}
    </Box>
  );
};

export default SupplierInvoiceUpload;
